import React, { useState } from 'react';
import { useAuthContext } from '../../context/AuthContext';
import './Auth.css';

export default function SignUp({ onSwitchToSignIn }) {
  const { signUp, signUpWithGoogle, isLoading, error, clearError } = useAuthContext();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  
  const [validationErrors, setValidationErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [signUpResult, setSignUpResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear auth error when user starts typing
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email is not valid';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const result = await signUp(formData.username, formData.email, formData.password);
      setSignUpResult(result);
      setShowConfirmation(true);
    } catch (err) {
      console.error('Sign up error:', err);
    }
  };

  const handleConfirmationSubmit = async (e) => {
    e.preventDefault();
    
    if (!confirmationCode.trim()) {
      return;
    }
    
    try {
      await signUpResult.user.confirmRegistration(confirmationCode, true);
      // Show success message and redirect to sign in
      setShowConfirmation(false);
      setSignUpResult(null);
      onSwitchToSignIn();
    } catch (err) {
      console.error('Confirmation error:', err);
    }
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || '';
  };

  const handleGoogleSignUp = async () => {
    try {
      await signUpWithGoogle();
    } catch (err) {
      console.error('Google sign up error:', err);
    }
  };

  if (showConfirmation) {
    return (
      <div className="auth-container">
        <div className="auth-header">
          <h2>Confirm Registration</h2>
          <p>A verification code has been sent to your email</p>
        </div>

        <form onSubmit={handleConfirmationSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="confirmationCode">Verification Code</label>
            <input
              type="text"
              id="confirmationCode"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              placeholder="Enter the verification code"
              disabled={isLoading}
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="auth-button primary"
            disabled={isLoading || !confirmationCode.trim()}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Confirming...
              </>
            ) : (
              <>
                <i className="fas fa-check"></i>
                Confirm Registration
              </>
            )}
          </button>
        </form>

        <div className="auth-links">
          <button
            type="button"
            className="link-button"
            onClick={() => setShowConfirmation(false)}
            disabled={isLoading}
          >
            Back to registration
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-header">
        <h2>Create Account</h2>
        <p>Sign up to access OmniContext</p>
      </div>

      {/* Google Sign Up Button */}
      <div className="oauth-section">
        <button
          type="button"
          className="oauth-button google"
          onClick={handleGoogleSignUp}
          disabled={isLoading}
        >
          <div className="oauth-icon">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </div>
          <span>Continue with Google</span>
        </button>
      </div>

      <div className="auth-divider">
        <span>or</span>
      </div>

      <form onSubmit={handleSubmit} className="auth-form">
        <div className="form-group">
          <label htmlFor="username">Username</label>
          <input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            className={getFieldError('username') ? 'error' : ''}
            placeholder="Choose a username"
            disabled={isLoading}
          />
          {getFieldError('username') && (
            <span className="error-message">{getFieldError('username')}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={getFieldError('email') ? 'error' : ''}
            placeholder="Enter your email"
            disabled={isLoading}
          />
          {getFieldError('email') && (
            <span className="error-message">{getFieldError('email')}</span>
          )}
        </div>

        <div className="form-group has-toggle">
          <label htmlFor="password">Password</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={getFieldError('password') ? 'error' : ''}
              placeholder="Create a secure password"
              disabled={isLoading}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
            </button>
          </div>
          {getFieldError('password') && (
            <span className="error-message">{getFieldError('password')}</span>
          )}
        </div>

        <div className="form-group has-toggle">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <div className="password-input-wrapper">
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={getFieldError('confirmPassword') ? 'error' : ''}
              placeholder="Confirm your password"
              disabled={isLoading}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              disabled={isLoading}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              <i className={showConfirmPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
            </button>
          </div>
          {getFieldError('confirmPassword') && (
            <span className="error-message">{getFieldError('confirmPassword')}</span>
          )}
        </div>

        {error && (
          <div className="auth-error">
            <i className="fas fa-exclamation-triangle"></i>
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          className="auth-button primary"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              Creating account...
            </>
          ) : (
            <>
              <i className="fas fa-user-plus"></i>
              Create Account
            </>
          )}
        </button>
      </form>

      <div className="auth-links">
        <div className="auth-divider">
          <span>Already have an account?</span>
        </div>
        
        <button
          type="button"
          className="link-button"
          onClick={onSwitchToSignIn}
          disabled={isLoading}
        >
          Sign In
        </button>
      </div>
    </div>
  );
}
